import React, { memo, useContext, useMemo } from 'react'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ActionSelector from 'Pods/ThreeDStudio/Shared/ActionSelector/ActionSelector'
import AIEngineSelector from 'Pods/ThreeDStudio/Shared/AIEngineSelector/AIEngineSelector'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  setInputImage,
  setInputViewImage,
  setIsMultiView,
  setPrompt,
} from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { setGenerationAction } from 'Pods/ThreeDStudio/Store/actions/schema'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import AdvancedSettings from './Components/AdvancedSettings/AdvancedSettings'
import ExtraQualitySelector from './Components/ExtraQualitySelector/ExtraQualitySelector'
import MaterialSelector from './Components/MaterialSelector/MaterialSelector'
import PolyCountSelector from './Components/PolyCountSelector/PolyCountSelector'
import PolygonTypeSelector from './Components/PolygonTypeSelector/PolygonTypeSelector'
import SmartLowPolySelector from './Components/SmartLowPolySelector/SmartLowPolySelector'
import UnlimitedPolycountSelector from './Components/UnlimitedPolycountSelector/UnlimitedPolycountSelector'
import UseCaseSelector from './Components/UseCaseSelector/UseCaseSelector'

const MULTI_VIEW_TAB_ID = 'multi-view-to-3d'

function ModelSettings() {
  const t = useTranslation()
  const generationAction = useThreeDStudioStore(state => state.schema.generationAction)
  const { multiViewEnabled, extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const isMultiView = useThreeDStudioStore(state => state.modelSettings?.isMultiView) && multiViewEnabled
  const isImageTo3D = generationAction === ACTION.IMAGE_TO_3D

  // Multi-view is still image-to-3d for the backend, so both image tabs share the
  // action and only the uploader mode differs. Behind the enable_multi_view flag the
  // tab is left out entirely and the image tab keeps the single uploader.
  const tabs = useMemo(() => ({
    image: {
      id: ACTION.IMAGE_TO_3D,
      onClick: () => {
        setGenerationAction(ACTION.IMAGE_TO_3D)
        setIsMultiView(false)
      },
      isActive: isImageTo3D && !isMultiView,
      setInputImage,
    },
    ...(multiViewEnabled && {
      multiView: {
        id: MULTI_VIEW_TAB_ID,
        onClick: () => {
          setGenerationAction(ACTION.IMAGE_TO_3D)
          setIsMultiView(true)
        },
        isActive: isImageTo3D && isMultiView,
        setInputViewImage,
      },
    }),
    text: {
      id: ACTION.TEXT_TO_3D,
      onClick: () => setGenerationAction(ACTION.TEXT_TO_3D),
      isActive: generationAction === ACTION.TEXT_TO_3D,
      placeholder: t('leftSide.modelSettings.promptPlaceholder'),
      setPrompt,
    },
  }), [isImageTo3D, isMultiView, multiViewEnabled, t])

  const items = useMemo(() => [
    {
      id: 'AIEngine',
      component: AIEngineSelector,
    },
    {
      id: 'ExtraQuality',
      component: ExtraQualitySelector,
    },
    {
      id: 'SmartLowPoly',
      component: SmartLowPolySelector,
    },
    {
      id: 'UnlimitedPolycount',
      component: UnlimitedPolycountSelector,
    },
    {
      id: 'PolygonType',
      component: PolygonTypeSelector,
    },
    {
      id: 'Polycount',
      component: PolyCountSelector,
    },
    {
      id: 'Material',
      component: MaterialSelector,
    },
  ], [])

  return (
    <div className="model-settings">
      <div className="model-settings__content">
        <ActionSelector
          id="model-settings"
          tabs={tabs}
          sliceName="modelSettings"
          storeSchemaKey="generationModelSchema"
        />
        <UseCaseSelector />
        {extractingCoreStyles ? (
          <>
            <AdvancedSettings forceState="closed" items={EMPTY_ARRAY} />
            <AdvancedSettings forceState="open" items={items} />
          </>
        ) : <AdvancedSettings items={items} />}
      </div>
    </div>
  )
}

export default memo(ModelSettings)
